import {
  CardActions,
  CardContent as MuiCardContent,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import StackedIcon from '@components/Icon/StackedIcon';

import useReferenceRange from '@hooks/useReferenceRange';
import { useScanResults } from '@hooks/useScanResults';

import { capitalize, formatDate } from '@utils/format';

import type { ReferenceRangeRiskLevel } from 'generated/types';

interface CardContentProps {
  /** Date when scan was performed */
  date: string | number | Date;
  /** Card footer content */
  actions?: React.ReactNode;
  title: {
    label: string;
    value: string;
    risk: ReferenceRangeRiskLevel;
  };
  header?: string | null;
}

export default function CardContent({
  children,
  date,
  actions,
  header,
  title,
}: React.PropsWithChildren<CardContentProps>) {
  const { icons } = useScanResults();
  const { parameterMap } = useReferenceRange();

  return (
    <>
      <MuiCardContent sx={{ pb: 0 }}>
        <Stack direction="column" justifyContent="flex-start" gap={1}>
          <Stack direction="column" justifyContent="flex-start">
            <Stack
              direction="row"
              justifyContent="space-between"
              flexWrap="wrap-reverse"
            >
              <Stack direction="row">
                <StackedIcon icons={icons} />
                <Stack direction="column" spacing={0}>
                  <Typography color="text.secondary" variant="subtitle2">
                    {header}
                  </Typography>
                  <Typography color="text.primary" variant="h6">
                    {title.label}
                    &nbsp;
                    <span
                      style={{
                        color: parameterMap[title.risk].colour,
                        fontWeight: 'bold',
                      }}
                    >
                      {capitalize(title.value)}
                    </span>
                  </Typography>
                </Stack>
              </Stack>
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{ alignSelf: 'flex-end' }}
              >
                {formatDate({ date: date, formatter: 'datetime' })}
              </Typography>
            </Stack>
          </Stack>
          {children}
        </Stack>
      </MuiCardContent>

      {actions && (
        <CardActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {actions}
        </CardActions>
      )}
    </>
  );
}
